import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Route : famille "tournée planifiée" (itinéraire d'arrêts ordonnés).
 *
 * Suite d'arrêts ordonnés à réaliser dans la journée par un intervenant ou un
 * véhicule : livraisons, interventions, collectes. Construite sur le modèle
 * "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas de tournée concrète ("tournée de livraison du
 * matin", "collecte hebdo") : il connaît la famille générique `route`. Le
 * vocabulaire métier est porté par le champ `type` (chaîne libre déclarée par
 * l'app), et l'état du cycle de vie par `status`.
 *
 * Affectation : une tournée vise un intervenant (`workerId`) OU un véhicule
 * (`vehicleAssetId`), les deux optionnels (aucun n'est le cœur de la famille).
 * `shiftId` rattache la tournée à la vacation qui la couvre, optionnel.
 *
 * Arrêts : `stops` est la liste ordonnée des points à visiter. Chaque arrêt est
 * un enregistrement libre (ordre, référence vers un `shipment` ou un
 * `work-order`, heure prévue...) : les liens shipment / work-order vivent DONC
 * à l'intérieur des arrêts, pas en tête de document (une tournée en visite
 * plusieurs). Les preuves de passage sont des ids de `document`
 * (`proofDocumentIds`).
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma).
 *
 * Accès : v0 reste org-wide (même doctrine que asset). Rien de sensible n'est
 * signalé sur cette famille.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille route (hors champs standard du socle). */
export const routeCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "livraison", "collecte"). */
  type: z.string().min(1),
  /** Intervenant qui exécute la tournée (référence `worker`), optionnel. */
  workerId: z.string().min(1).optional(),
  /** Véhicule affecté à la tournée (référence `asset`), optionnel. */
  vehicleAssetId: z.string().min(1).optional(),
  /** Vacation de rattachement (référence `shift`), optionnel. */
  shiftId: z.string().min(1).optional(),
  /** Jour planifié de la tournée, optionnel. */
  date: z.date().optional(),
  /** Arrêts ordonnés (ordre, référence shipment/work-order, heure prévue...). Liste structurée, optionnel. */
  stops: z.array(z.record(z.string(), z.unknown())).optional(),
  /** État courant, chaîne libre pilotée par l'app (ex. "planifiee", "en_cours", "terminee"). Optionnel. */
  status: z.string().min(1).optional(),
  /** Preuves de passage / livraison (références `document`). Liste d'ids, optionnel. */
  proofDocumentIds: z.array(z.string()).optional(),
} as const;

/** Schéma complet de la famille route (champs standard + champs métier). */
export const RouteSchema = z.object({
  ...baseEntityFields,
  ...routeCoreFields,
});

/** Type d'une route. */
export type Route = z.infer<typeof RouteSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const RouteCreateInputSchema = z.object({
  ...routeCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une route. */
export type RouteCreateInput = z.infer<typeof RouteCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const RouteUpdateInputSchema = z
  .object({ ...routeCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une route. */
export type RouteUpdateInput = z.infer<typeof RouteUpdateInputSchema>;
